import { __spreadArray, __assign } from 'tslib';
import { useState, useEffect } from 'react';
import { ImportStatus } from '@keepeek/commons';
import cloneDeep from 'lodash/cloneDeep';
import { useSnackbar } from 'notistack';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FormFieldKey } from '../providers/formFields/atoms.js';
import { useFormFields } from '../providers/formFields/hooks.js';
import { IndexationStep } from '../providers/indexation/atoms.js';
import { useIndexation } from '../providers/indexation/hooks.js';
import { useUpload } from '../providers/upload/hooks/useUpload.js';
import { UploadStatus } from '../providers/upload/selectors.js';

function useKFImport(_a) {
    var internalFieldNames = _a.internalFieldNames, formTypeFormIdMappings = _a.formTypeFormIdMappings, uploadKey = _a.uploadKey, overrideClassificationStep = _a.overrideClassificationStep, indexInAllAvailableLocale = _a.indexInAllAvailableLocale, classificationDestination = _a.classificationDestination;
    var t = useTranslation("refront-components").t;
    var formFields = useFormFields({
        internalFieldNames: internalFieldNames,
        contextKey: FormFieldKey.IMPORT
    }).formFields;
    // We need to use react hook form with "all" with MUI to work :
    // https://github.com/react-hook-form/react-hook-form/discussions/2357#discussioncomment-42162
    var reactHookFormMethods = useForm({ mode: "all" });
    var _b = useUpload(uploadKey), kpkMediaImports = _b.kpkMediaImports, uploadStatus = _b.uploadStatus, uploadEnded = _b.uploadEnded;
    var _c = useState([]), alreadyFilesUploadErrorDisplayedUuid = _c[0], setAlreadyFilesUploadErrorDisplayedUuid = _c[1];
    var enqueueSnackbar = useSnackbar().enqueueSnackbar;
    var _d = useIndexation({
        formTypeFormIdMappings: formTypeFormIdMappings,
        kpkMediaImports: kpkMediaImports,
        overrideClassificationStep: overrideClassificationStep,
        indexInAllAvailableLocale: indexInAllAvailableLocale,
        classificationDestination: classificationDestination
    }), process = _d.process, processStep = _d.processStep, resetIndexation = _d.reset, indexationInError = _d.error, indexationRightError = _d.rightError, indexationNeedNewUploadErrors = _d.needNewUploadErrors;
    var isUploadFinished = uploadStatus === UploadStatus.SUCCESS ||
        uploadStatus === UploadStatus.SUCCESS_WITH_ERRORS ||
        uploadStatus === UploadStatus.ERROR;
    useEffect(function () {
        // display error message if upload fail :
        var filesUploadInError = kpkMediaImports.filter(function (file) { return file.status === ImportStatus.ERROR; });
        // only display error on NEW file upload fail :
        var filesUploadInErrorToDisplay = filesUploadInError.filter(function (f) { return !alreadyFilesUploadErrorDisplayedUuid.includes(f.uuid); });
        if (isUploadFinished && filesUploadInErrorToDisplay.length > 0) {
            enqueueSnackbar(t("components.kf-import.upload.error", {
                fileNames: filesUploadInErrorToDisplay.map(function (f) { return f.originalFileName; }),
                count: filesUploadInErrorToDisplay.length,
                joinArrays: ", "
            }), {
                variant: "error"
            });
            setAlreadyFilesUploadErrorDisplayedUuid(__spreadArray([], filesUploadInError.map(function (f) { return f.uuid; }), true));
        }
    }, [alreadyFilesUploadErrorDisplayedUuid, enqueueSnackbar, isUploadFinished, kpkMediaImports, t]);
    useEffect(function () {
        if (indexationNeedNewUploadErrors && indexationNeedNewUploadErrors.length > 0) {
            enqueueSnackbar(t("components.kf-import.upload.error", {
                fileNames: indexationNeedNewUploadErrors,
                count: indexationNeedNewUploadErrors.length,
                joinArrays: ", "
            }), {
                variant: "error"
            });
        }
        else if (indexationRightError) {
            enqueueSnackbar(t("components.kf-import.indexation.right-error"), {
                variant: "error"
            });
        }
        else if (indexationInError) {
            enqueueSnackbar(t("components.kf-import.indexation.error"), {
                variant: "error"
            });
        }
    }, [enqueueSnackbar, indexationInError, indexationNeedNewUploadErrors, indexationRightError, t]);
    var processIndexation = function () {
        var filesToIndex = kpkMediaImports.filter(function (file) { return file.status === ImportStatus.SUCCESS; });
        if (filesToIndex.length === 0) {
            enqueueSnackbar(t("components.kf-import.indexation.error.noFile"), {
                variant: "error"
            });
        }
        else {
            reactHookFormMethods.handleSubmit(function () {
                _processIndexation();
            }, function () {
                enqueueSnackbar(t("components.kf-import.indexation.validation.error"), {
                    variant: "info"
                });
            })();
        }
    };
    function _processIndexation() {
        // need to merge values from react-hook-form and formFields :
        var mergedFormFields = cloneDeep(formFields).map(function (ff) {
            return __assign(__assign({}, ff), { value: reactHookFormMethods.getValues(ff.id) });
        });
        process(mergedFormFields);
    }
    var reset = function () {
        uploadEnded();
        resetIndexation();
        setAlreadyFilesUploadErrorDisplayedUuid([]);
        reactHookFormMethods.reset();
    };
    return {
        formFields: formFields,
        kpkMediaImports: kpkMediaImports,
        processIndexation: processIndexation,
        isIndexationFinished: IndexationStep.FINISHED === processStep,
        isIndexationInProgress: IndexationStep.FINISHED !== processStep && IndexationStep.INITIAL !== processStep,
        isUploadFinished: isUploadFinished,
        isIndexationInError: indexationInError,
        reset: reset,
        reactHookFormMethods: reactHookFormMethods
    };
}

export { useKFImport };
//# sourceMappingURL=hooks.js.map
